import { ReactNode } from 'react';
import { Status } from '@optimus-web/common';
import { useId } from '@optimus-web/helpers';
import { FormLabel } from '../common/FormLabel';
import { FormMessage } from '../common/FormMessage';
import { FieldHeader } from '../Field/style';
import { Grid } from '../Grid';
import { GridProps } from '../Grid/types';
import { SelectionCard } from '../SelectionCard/SelectionCard';
import { Direction } from '../SelectionCard/types';
import { SelectionCardGroupContainer } from './style';

export interface Option {
    label: ReactNode;
    value: string;
    name: string;
    description?: ReactNode;
    media?: ReactNode;
    checked?: boolean;
    dataTestId?: string;
    disabled?: boolean;
    cornerRadius?: 'small' | 'medium';
    showSelector?: boolean;
}

interface SelectionCardGroupCommonProps
    extends GridProps,
        Omit<React.HTMLAttributes<HTMLDivElement | HTMLFieldSetElement>, 'onChange'> {
    options: Array<Option>;
    cardVariant?: Direction;
    disabled?: boolean;
    required?: boolean;
    captionIconWithTooltip?: ReactNode;
    helper?: React.ReactNode;
    error?: React.ReactNode;
    label?: React.ReactNode;
    cornerRadius?: 'small' | 'medium';
    showSelector?: boolean;
    compact?: boolean;
    size?: 'medium' | 'large';
}
interface RadioSelectionCardGroupProps extends SelectionCardGroupCommonProps {
    type: 'radio';
    onChange: (e: React.ChangeEvent<HTMLInputElement>, value: string) => void;
    inputValue: string;
}
interface CheckboxSelectionCardGroupProps extends SelectionCardGroupCommonProps {
    type: 'checkbox';
    onChange: (e: React.SyntheticEvent<HTMLElement>, value: string[]) => void;
    inputValue: string[];
}

export type SelectionCardGroupProps = CheckboxSelectionCardGroupProps | RadioSelectionCardGroupProps;

export const SelectionCardGroup = ({
    options,
    cardVariant = 'horizontal',
    disabled = false,
    type,
    onChange,
    inputValue,
    label: groupLabel,
    helper,
    error,
    required,
    captionIconWithTooltip,
    cornerRadius = 'small',
    showSelector = true,
    compact = false,
    size = 'medium',
    templateColumns = cardVariant === 'vertical' ? `repeat(${options.length}, 1fr)` : '1fr',
    ...other
}: SelectionCardGroupProps) => {
    const labelId = useId('selection-card-group-label');
    const helperId = useId('selection-card-group-helper');
    const errorId = useId('selection-card-group-error');
    const radioGroupName = useId('selection-card-group-radio');
    const ariaLabelledbyIds = [groupLabel ? labelId : null, helper ? helperId : null, error ? errorId : null]
        .filter(Boolean)
        .join(' ');

    const handleChange =
        (handledValue: string) =>
        (event: React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent<HTMLElement>, checked: boolean) => {
            if (onChange) {
                if (type === 'checkbox') {
                    const newValue = checked
                        ? [...inputValue, handledValue]
                        : inputValue.filter((item) => handledValue !== item);
                    onChange(event, newValue);
                } else {
                    onChange(event as React.ChangeEvent<HTMLInputElement>, handledValue);
                }
            }
        };

    const result = options.map(({ label, value, description, media, name, dataTestId, disabled: optionDisabled }) => (
        <SelectionCard
            label={label}
            value={value}
            name={type === 'radio' ? radioGroupName : name}
            description={description}
            media={media}
            $direction={cardVariant}
            disabled={disabled || !!optionDisabled}
            type={type}
            checked={type === 'radio' ? inputValue === value : inputValue.includes(value)}
            key={value}
            onChange={handleChange(value)}
            dataTestId={dataTestId}
            cornerRadius={cornerRadius}
            showSelector={showSelector}
        />
    ));
    return (
        <SelectionCardGroupContainer
            disabled={disabled}
            aria-labelledby={ariaLabelledbyIds}
            aria-errormessage={error ? errorId : undefined}
            aria-invalid={!!error}
            {...other}
        >
            {groupLabel ? (
                <FieldHeader size={size}>
                    <FormLabel required={required} as="legend" id={labelId}>
                        {groupLabel}
                    </FormLabel>
                    {captionIconWithTooltip}
                </FieldHeader>
            ) : null}

            <Grid
                {...other}
                autoRows={cardVariant === 'horizontal' ? '1fr' : 'auto'}
                templateRows={cardVariant === 'horizontal' ? 'none' : 'auto'}
                gap={compact ? '100' : '200'}
                templateColumns={templateColumns}
            >
                {result}
            </Grid>

            {helper ? (
                <FormMessage size="medium" data-test-message-helper id={helperId}>
                    {helper}
                </FormMessage>
            ) : null}
            {error ? (
                <FormMessage size="medium" status={Status.Error} data-test-message-error id={errorId}>
                    {error}
                </FormMessage>
            ) : null}
        </SelectionCardGroupContainer>
    );
};
